// Contract History (activity log) display helpers.
export const HISTORY_TYPE_SEVERITY: Record<
  string,
  "success" | "info" | "warning" | "danger"
> = {
  Created: "success",
  Updated: "info",
  ProceduresRegenerated: "warning",
  AttachmentAdded: "success",
  AttachmentRemoved: "danger",
  CarveoutAdded: "success",
  CarveoutRemoved: "danger",
};

export const HISTORY_TYPE_LABELS: Record<string, string> = {
  Created: "Created",
  Updated: "Updated",
  ProceduresRegenerated: "Procedures Regenerated",
  AttachmentAdded: "Attachment Added",
  AttachmentRemoved: "Attachment Removed",
  CarveoutAdded: "Carve-out Added",
  CarveoutRemoved: "Carve-out Removed",
  ProceduresGenerated: "Procedures Generated",
};

export const HISTORY_FIELD_LABELS: Record<string, string> = {
  providerId: "Provider",
  payerId: "Payer",
  effectiveDate: "Effective Date",
  nextUpdateDate: "Next Update Date",
  notes: "Notes",
  mpdrRulesetIds: "MPPR Rulesets",
  caseRateRulesetIds: "Case Rate Rulesets",
  explodingFeeScheduleRulesetIds: "Exploding Fee Schedule Rulesets",
  packageRulesetIds: "Package Rulesets",
  inpatientRulesetIds: "Inpatient Rulesets",
  medicareAdvantagePayerIds: "Medicare Advantage Payers",
  rateCalculations: "Rate Calculations",
  carveoutRate: "Carve-out Rate",
};

/** Render a `{ ratePercent, rateType }` pair as `$12.50/unit` or `85%`. */
export const formatRate = (
  ratePercent: number | null | undefined,
  rateType: unknown
): string => {
  if (ratePercent === null || ratePercent === undefined) return "Not set";
  const value = Number(ratePercent);
  return rateType === "unit"
    ? `$${(value / 100).toFixed(2)}/unit`
    : `${value}%`;
};

/** Render a history `from`/`to` value as readable text (handles provider/payer name objects, arrays, rate calcs). */
export const formatHistoryValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "Not set";
  if (Array.isArray(value)) {
    if (value.length === 0) return "None";
    return value
      .map((v) => {
        if (v && typeof v === "object") {
          const o = v as any;
          if (o.name) return o.name as string;
          if (o.ratePercent !== undefined) {
            return formatRate(o.ratePercent, o.rateType);
          }
          // Id-bearing object whose name didn't resolve (e.g. deleted ruleset).
          if (o.id !== undefined) return "Unnamed";
          return JSON.stringify(o);
        }
        return String(v);
      })
      .join(", ");
  }
  if (typeof value === "object") {
    const o = value as any;
    if (o.name) return o.name as string;
    // Carve-out rate edits (CLE-2573) log a bare { ratePercent, rateType } pair.
    if (o.ratePercent !== undefined)
      return formatRate(o.ratePercent, o.rateType);
    if (o.id !== undefined) return "Unnamed";
    return JSON.stringify(o);
  }
  return String(value);
};

/**
 * Readable label for a single rate calculation: base rate file name + its rate.
 * The name comes from the history snapshot itself (`displayName`), which now
 * carries the base rate file name alongside the ids.
 */
const formatRateCalculationLabel = (calc: any): string => {
  const name =
    calc?.displayName?.trim() ||
    calc?.baseRateFile?.displayName?.trim() ||
    "Unnamed";
  if (calc?.ratePercent === undefined || calc?.ratePercent === null) {
    return name;
  }
  const ratePercent = Number(calc.ratePercent);
  const rate =
    calc.rateType === "unit"
      ? `$${(ratePercent / 100).toFixed(2)}/unit`
      : `${ratePercent}%`;
  return `${name} ${rate}`;
};

/**
 * Diff two rate-calculation array snapshots (history `from`/`to`), identified by
 * base rate file. Names come from each snapshot's `displayName`.
 *
 * `changed` covers files that stayed attached but had their rate corrected
 * (CLE-2573) — without it an in-place rate edit reads as "No changes".
 */
export const diffRateCalculations = (
  from: unknown,
  to: unknown
): { added: string[]; removed: string[]; changed: string[] } => {
  const fromArr = Array.isArray(from) ? (from as any[]) : [];
  const toArr = Array.isArray(to) ? (to as any[]) : [];
  const keyOf = (calc: any): string => calc?.baseRateFileId;
  const fromKeys = new Set(fromArr.map(keyOf));
  const toByKey = new Map(toArr.map((calc) => [keyOf(calc), calc] as const));
  const sameRate = (a: any, b: any): boolean =>
    Number(a?.ratePercent) === Number(b?.ratePercent) &&
    (a?.rateType ?? "percent") === (b?.rateType ?? "percent");

  return {
    added: toArr
      .filter((calc) => !fromKeys.has(keyOf(calc)))
      .map(formatRateCalculationLabel),
    removed: fromArr
      .filter((calc) => !toByKey.has(keyOf(calc)))
      .map(formatRateCalculationLabel),
    changed: fromArr
      .filter((calc) => {
        const next = toByKey.get(keyOf(calc));
        return next !== undefined && !sameRate(calc, next);
      })
      .map((calc) => {
        const next = toByKey.get(keyOf(calc));
        return `${formatRateCalculationLabel(calc)} → ${formatRate(
          next?.ratePercent,
          next?.rateType
        )}`;
      }),
  };
};

/** History fields that are named lists (e.g. rulesets) — shown as added/removed diffs. */
export const HISTORY_LIST_FIELDS = new Set([
  "mpdrRulesetIds",
  "caseRateRulesetIds",
  "explodingFeeScheduleRulesetIds",
  "packageRulesetIds",
  "inpatientRulesetIds",
  "medicareAdvantagePayerIds",
]);

/**
 * Diff two snapshots of a named list (history `from`/`to`) into added/removed
 * label lists. Items are identified by `id` (falling back to their name/value)
 * and labelled by `name`.
 */
export const diffNamedList = (
  from: unknown,
  to: unknown
): { added: string[]; removed: string[] } => {
  const fromArr = Array.isArray(from) ? (from as any[]) : [];
  const toArr = Array.isArray(to) ? (to as any[]) : [];
  const keyOf = (item: any): string =>
    item && typeof item === "object"
      ? item.id ?? item.name ?? JSON.stringify(item)
      : String(item);
  const labelOf = (item: any): string => {
    if (!item || typeof item !== "object") return String(item);
    const name = item.name ?? "Unnamed";
    if (item.providerName || item.payerName) {
      return `${name} (${item.providerName ?? ""}-${item.payerName ?? ""})`;
    }
    return name;
  };
  const fromKeys = new Set(fromArr.map(keyOf));
  const toKeys = new Set(toArr.map(keyOf));
  return {
    added: toArr.filter((item) => !fromKeys.has(keyOf(item))).map(labelOf),
    removed: fromArr.filter((item) => !toKeys.has(keyOf(item))).map(labelOf),
  };
};
